import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Bookmark,
  Pencil,
  Calendar,
  Clock,
  ExternalLink,
  Mail,
  Share2,
  Tag,
  Type,
  User
} from "lucide-react";
import { AdSlot } from "../../components/AdSlot";
import { ArticleCard } from "../../components/ArticleCard";
import { CommentsSection } from "../../components/CommentsSection";
import { EmptyState, ErrorState, LoadingState } from "../../components/LoadingState";
import { NewsletterBox } from "../../components/NewsletterBox";
import { Seo } from "../../components/Seo";
import { SocialShareBar } from "../../components/SocialShareBar";
import { useApi } from "../../hooks/useApi";
import type { Advertisement, Article } from "../../types";
import { formatDate } from "../../utils/date";
import { useAuth } from "../../services/auth";

export function ArticlePage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const canEdit = Boolean(user && (user.permissions.includes("*") || user.permissions.includes("articles.manage") || user.roles.some((role) => ["Super Administrator", "Administrator", "admin"].includes(role))));
  const { data: article, loading, error } = useApi<Article>(`/api/articles/public/${slug}`, [slug]);
  const { data: topAds } = useApi<Advertisement[]>("/api/advertisements/placement/article_top", []);
  const { data: midAds } = useApi<Advertisement[]>("/api/advertisements/placement/article_middle", []);
  const { data: bottomAds } = useApi<Advertisement[]>("/api/advertisements/placement/article_bottom", []);

  const [readingProgress, setReadingProgress] = useState(0);
  const [fontSize, setFontSize] = useState<"normal" | "large">("normal");
  const [bookmarked, setBookmarked] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  // Track reading progress on scroll
  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (totalHeight > 0) {
        const progress = Math.min(100, Math.max(0, (window.scrollY / totalHeight) * 100));
        setReadingProgress(progress);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (loading) return <LoadingState label="Loading story from the archives..." />;
  if (error) return <ErrorState message={error} />;
  if (!article) return <EmptyState title="Story unavailable" body="This article is not published or has moved." />;

  const articleUrl = typeof window !== "undefined" ? window.location.href : `https://zimsmart.co.zw/article/${article.slug}`;

  return (
    <>
      <Seo
        title={`${article.seo?.title || article.title} | ZimSmart Magazine`}
        description={article.seo?.description || article.excerpt || ""}
        canonical={article.seo?.canonical}
        image={article.image?.url}
        socialTitle={String(article.seo?.openGraph?.title || "")}
        socialDescription={String(article.seo?.openGraph?.description || "")}
        socialImage={String(article.seo?.openGraph?.image || article.image?.url || "")}
        robots={article.seo?.robots}
        type="article"
        structuredData={article.seo?.structuredData}
      />

      {/* Dynamic Reading Progress Bar at Top of Viewport */}
      <div
        className="mag-reading-progress-bar"
        style={{ transform: `scaleX(${readingProgress / 100})` }}
        aria-hidden="true"
      />

      <div className="mag-article-layout">
        {/* Article Breadcrumb & Kicker Bar */}
        <div className="mag-container">
          <div className="mag-article-top-nav">
            <nav className="mag-breadcrumbs" aria-label="Breadcrumb">
              <Link to="/">Cover</Link>
              <span className="sep">/</span>
              <Link to={`/${article.category.slug}`}>{article.category.name}</Link>
              <span className="sep">/</span>
              <span className="current">Story</span>
            </nav>
            <div className="mag-article-admin-actions">
              {canEdit && <Link className="mag-article-edit-link" to={`/admin/articles/${article.id}/edit`}><Pencil size={15} /> Edit Post</Link>}
              <span className="mag-editorial-tag">ZIMSMART EXCLUSIVE REPORT</span>
            </div>
          </div>

          {/* Top Article Billboard / Sponsor Ad */}
          <AdSlot ads={topAds} placement="article_top" className="mb-4" />

          {/* Article Grand Header */}
          <header className="mag-article-header">
            <div className="mag-kicker-row">
              <Link className={`mag-kicker-large mag-cat-kicker-${article.category.slug}`} to={`/category/${article.category.slug}`}>
                <span className="mag-kicker-dot" />
                <span>{article.category.name}</span>
              </Link>
              {article.disclosure_label && article.disclosure_label !== "editorial" ? (
                <span className="mag-disclosure-badge">
                  {article.disclosure_label.replace("_", " ")}
                </span>
              ) : null}
            </div>

            <h1 className="mag-article-title">{article.title}</h1>

            {article.subtitle && (
              <p className="mag-article-dek">{article.subtitle}</p>
            )}

            {/* Author Byline & Meta */}
            <div className="mag-article-byline-bar">
              <div className="mag-byline-author">
                <div className="mag-author-avatar-placeholder">
                  <User size={18} />
                </div>
                <div>
                  <div className="mag-byline-names">
                    <span className="by-label">By</span>{" "}
                    <Link to={`/author/${article.author.slug}`} className="author-link">
                      {article.author.name}
                    </Link>
                  </div>
                  <div className="mag-byline-details">
                    <span>Published {formatDate(article.published_at)}</span>
                    <span className="dot">•</span>
                    <span>{article.reading_time} min read</span>
                  </div>
                </div>
              </div>

              {/* Top Social Sharing Buttons */}
              <div className="mag-header-share-actions">
                <button
                  type="button"
                  onClick={() => setFontSize((f) => (f === "normal" ? "large" : "normal"))}
                  className="mag-share-icon-btn font-toggle"
                  title="Toggle larger reading type"
                >
                  <Type size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => setBookmarked((b) => !b)}
                  className={`mag-share-icon-btn bookmark ${bookmarked ? "active text-amber-600" : ""}`}
                  title={bookmarked ? "Saved to reading list" : "Bookmark this story"}
                >
                  <Bookmark size={16} fill={bookmarked ? "currentColor" : "none"} />
                </button>
                <SocialShareBar
                  title={article.title}
                  url={articleUrl}
                  excerpt={article.excerpt || ""}
                  variant="compact"
                />
              </div>
            </div>
          </header>
        </div>

        {/* Full-width Featured Imagery */}
        {article.image?.url && (
          <div className="mag-container">
            <figure className="mag-featured-figure">
              <div className="mag-featured-frame">
                <img
                  src={article.image.url}
                  alt={article.image.alt_text || article.title}
                  className="mag-featured-img"
                />
              </div>
              {(article.image.caption || article.image.credit) && (
                <figcaption className="mag-featured-caption">
                  {article.image.caption && <span>{article.image.caption}</span>}
                  {article.image.credit && (
                    <span className="mag-photo-credit"> Photography: {article.image.credit}</span>
                  )}
                </figcaption>
              )}
            </figure>
          </div>
        )}

        {/* Main Editorial Text & Sidebar */}
        <div className="mag-container">
          <div className="mag-article-body-grid">
            {/* Sticky Share & Engagement Rail (Twitter, WhatsApp, LinkedIn, Facebook, Copy) */}
            <SocialShareBar
              title={article.title}
              url={articleUrl}
              excerpt={article.excerpt || ""}
              variant="sticky-rail"
            />

            {/* Article Prose Content */}
            <main className={`mag-prose-column ${fontSize === "large" ? "prose-large-text" : ""}`}>
              {/* Drop Cap styled first paragraph through CSS */}
              {article.custom_css ? <style>{`@scope (.article-custom-${article.id}) { ${article.custom_css} }`}</style> : null}
              <div
                className={`mag-article-prose article-custom-${article.id}`}
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Mid-Article Inline Ad / Sponsor Box */}
              <AdSlot ads={midAds} placement="article_middle" className="my-8" />

              {/* Tags Strip */}
              {article.tags?.length > 0 && (
                <div className="mag-article-tags-wrap">
                  <div className="mag-tags-title">
                    <Tag size={15} />
                    <span>TOPICS COVERED:</span>
                  </div>
                  <div className="mag-tags-list">
                    {article.tags.map((tag) => (
                      <Link key={tag.id} to={`/tag/${tag.slug}`} className="mag-tag-chip">
                        #{tag.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Full Social Sharing Ribbon at conclusion */}
              <div className="mag-article-end-share-block">
                <SocialShareBar
                  title={article.title}
                  url={articleUrl}
                  excerpt={article.excerpt || ""}
                  variant="horizontal"
                />
              </div>

              {/* Author Bio Signature Box */}
              <div className="mag-author-bio-card">
                <div className="mag-author-bio-avatar">
                  <User size={28} />
                </div>
                <div className="mag-author-bio-content">
                  <span className="mag-bio-badge">ZIMSMART JOURNALIST</span>
                  <h4 className="mag-bio-name">
                    <Link to={`/author/${article.author.slug}`}>{article.author.name}</Link>
                  </h4>
                  <p className="mag-bio-text">
                    {article.author.biography ||
                      "Senior journalist and editorial correspondent at ZimSmart Magazine covering emerging technologies, economic trends, and African industry."}
                  </p>
                  {article.author.email && (
                    <a href={`mailto:${article.author.email}`} className="mag-bio-email">
                      <Mail size={13} className="inline mr-1" />
                      <span>Contact Reporter</span>
                    </a>
                  )}
                </div>
              </div>

              {/* Bottom Article Ad Slot */}
              <AdSlot ads={bottomAds} placement="article_bottom" className="mt-8" />

              {/* Reader Comments & Editorial Discussion (Requires Login) */}
              <CommentsSection article={article} />
            </main>
          </div>

          {/* Related Stories Section */}
          {article.related && article.related.length > 0 && (
            <section className="mag-related-section" aria-label="Related Coverage">
              <div className="mag-section-header">
                <div className="mag-section-title-wrap">
                  <span className="mag-section-indicator" />
                  <h2 className="mag-section-title">MORE FROM THE ZIMSMART NEWSROOM</h2>
                </div>
                <Link to={`/${article.category.slug}`} className="mag-view-all-link">
                  <span>More {article.category.name}</span>
                </Link>
              </div>
              <div className="mag-articles-grid">
                {article.related.map((item) => (
                  <ArticleCard key={item.id} article={item} variant="compact" />
                ))}
              </div>
            </section>
          )}

          {/* Newsletter Box */}
          <NewsletterBox />
        </div>
      </div>
    </>
  );
}
